import { useEffect, useState } from 'react';
import { Maps, Timeline } from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';

import { useRideEditor } from '../../hooks/useRideEditor';

import { INITIAL_ZOOM_LEVEL } from './RouteMap.helpers';
import { RATimelineLocationDot, RATimelineNumberDot, RouteMapContainer } from './RouteMap.styles';
import { useDefaultRouteDetails } from './useDefaultRouteDetails';
import { useMapCenter } from './useMapCenter';
import { useRouteMapDetails } from './useRouteMapDetails';

export const RouteMap: React.FC = () => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();
  const {
    waypointsOnMap,
    raWaypointOnMap,
    handleEstimatedTime,
    completeAnchorTime,
    suggestedRouteDetails,
    suggestionData,
  } = useRideEditor();
  const { center } = useMapCenter({ useRideEditor, mapInstance });
  const defaultRouteDetails = useDefaultRouteDetails({
    isMapReady: !!mapInstance,
    overviewPolyline: suggestedRouteDetails?.overviewPolyline,
    legs: suggestedRouteDetails?.legs,
  });
  const { routeDetails } = useRouteMapDetails({
    isMapReady: !!mapInstance,
    waypointsOnMap,
    completeAnchorTime,
    defaultRouteDetails: defaultRouteDetails,
    raWaypointOnMap,
  });

  const splitLatLng = (latLng) => {
    const latLngArray = latLng?.split(',');

    if (latLngArray) {
      return { lat: parseFloat(latLngArray[0]), lng: parseFloat(latLngArray[1]) };
    }

    return null;
  };

  useEffect(() => {
    routeDetails && routeDetails.legs.length && handleEstimatedTime(routeDetails.legs);
  }, [routeDetails?.legs]);

  return (
    <RouteMapContainer>
      <Maps
        googleMapsApiKey={window.env?.RIDEIQ_GOOGLE_MAPS_API_KEY}
        handleMapInstance={(newMapInstance) => setMapInstance(newMapInstance)}
        center={center}
        zoom={INITIAL_ZOOM_LEVEL}
        options={{
          cameraControlOptions: {
            position: 3,
          },
        }}
      >
        {raWaypointOnMap && raWaypointOnMap.type === 'ra_start' && splitLatLng(raWaypointOnMap.latLng) && (
          <Maps.Marker position={splitLatLng(raWaypointOnMap.latLng) || undefined}>
            <RATimelineLocationDot num={1} />
          </Maps.Marker>
        )}
        {waypointsOnMap
          .filter((waypoint) => waypoint.type !== 'ra_start' && waypoint.type !== 'ra_end')
          .map((waypoint, index) => {
            const latLng = splitLatLng(waypoint.latLng);
            const isSmartPoolingSuggestion =
              !waypoint.anchor &&
              !!suggestionData?.waypointsAttributes.find(
                (suggestedWaypoint) => suggestedWaypoint.location.placeId === waypoint?.location?.placeId
              );

            return waypoint.location && latLng ? (
              <Maps.Marker key={index} position={latLng}>
                {waypoint.type === 'pick_up' ? (
                  <Timeline.LocationDot
                    num={raWaypointOnMap && raWaypointOnMap.type === 'ra_start' ? index + 2 : index + 1}
                    highlight={isSmartPoolingSuggestion}
                  />
                ) : (
                  <Timeline.NumberDot
                    num={raWaypointOnMap && raWaypointOnMap.type === 'ra_start' ? index + 2 : index + 1}
                    highlight={isSmartPoolingSuggestion}
                  />
                )}
              </Maps.Marker>
            ) : null;
          })}
        {raWaypointOnMap && raWaypointOnMap.type === 'ra_end' && splitLatLng(raWaypointOnMap.latLng) && (
          <Maps.Marker position={splitLatLng(raWaypointOnMap.latLng) || undefined}>
            <RATimelineNumberDot
              num={
                waypointsOnMap.filter((waypoint) => waypoint.type !== 'ra_start' && waypoint.type !== 'ra_end').length +
                1
              }
            />
          </Maps.Marker>
        )}
        {routeDetails && routeDetails.plannedPath.length > 0 && (
          <Maps.Polyline
            options={{
              path: routeDetails.plannedPath,
              geodesic: true,
              strokeColor: colorTokens.secondary.main,
              strokeOpacity: 1.0,
              strokeWeight: 3,
              zIndex: 1,
            }}
          />
        )}
      </Maps>
    </RouteMapContainer>
  );
};
